import {
  Link,
  Divider,
  BulletSymbol,
  BulletText,
  BulletPoint,
  List,
  ContentContainer,
  LinkLight,
  DividerLight,
  BulletSymbolLight,
  BulletTextLight,
  BulletPointLight,
  ListLight,
  ContentContainerLight,
  CopyLight,
  Copy,
} from '@/shared/styles/styledComponents/legalTextComponents';
import BackButton from '@/shared/components/BackButton';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import React from 'react';
import { Linking } from 'react-native';
import { EMME_SUPPORT_EMAIL } from '@/shared/constants/utility';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';

const AAA_WEBSITE = 'https://www.adr.org';
const AAA_MEDIATION_UNDER_10K =
  'https://services.adr.org/eroom/faces/welcome_and_steps.jspx';
const AAA_MEDIATION_10K_PLUS =
  'https://www.adr.org/aaa/faces/services/disputeresolutionservices';

const Terms = ({ isModal }: { isModal?: boolean }) => {
  const { isDesktop } = useBreakpoint();
  const router = useRouter();

  const openSupportEmail = () => {
    Linking.openURL(`mailto:${EMME_SUPPORT_EMAIL}`);
  };

  const openDisputeContactEmail = () => {
    Linking.openURL('mailto:erica@emmestyling.com');
  };

  const openAaaWebsite = () => {
    Linking.openURL(AAA_WEBSITE);
  };

  const openAaaMediationUnder10k = () => {
    Linking.openURL(AAA_MEDIATION_UNDER_10K);
  };

  const openAaaMediation10kPlus = () => {
    Linking.openURL(AAA_MEDIATION_10K_PLUS);
  };

  const navigateToPrivacyPolicy = () => {
    router.push(ROUTES.PRIVACY.path);
  };

  const SelectedContentContainer = !isModal
    ? ContentContainer
    : ContentContainerLight;
  const SelectedCopy = !isModal ? Copy : CopyLight;
  const SelectedDivider = !isModal ? Divider : DividerLight;
  const SelectedLink = !isModal ? Link : LinkLight;
  const SelectedBulletSymbol = !isModal ? BulletSymbol : BulletSymbolLight;
  const SelectedBulletText = !isModal ? BulletText : BulletTextLight;
  const SelectedBulletPoint = !isModal ? BulletPoint : BulletPointLight;
  const SelectedList = !isModal ? List : ListLight;

  return (
    <SelectedContentContainer isDesktop={isDesktop}>
      {!isModal && <BackButton containerStyle={{ marginBottom: 16 }} />}
      <SelectedCopy bold style={{ marginBottom: 16 }}>
        Terms of Service
      </SelectedCopy>
      <SelectedCopy>Updated: March 1, 2026</SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        A. Introductory Terms
      </SelectedCopy>

      <SelectedCopy bold style={{ marginBottom: 12 }}>
        1. Acceptance of Terms
      </SelectedCopy>
      <SelectedList>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            Welcome to EMME Styling (&ldquo;EMME Styling&rdquo;,
            &ldquo;Service&rdquo; or &ldquo;Site&rdquo;). By signing up on the
            account registration or using the Site in any way, you are agreeing
            to comply with and be bound by this Agreement, the Privacy Policy
            and all rules, policies, and disclaimers posted on the Site or about
            which you are notified (collectively &ldquo;Terms&rdquo; or the
            &ldquo;Agreement&rdquo;). Please review all of the Terms carefully
            before using the Site. These Terms apply to your use of the Service
            whether through the mobile application or otherwise. Any reference
            to the &ldquo;Site&rdquo; or the EMME Styling &ldquo;website&rdquo;
            means the Service whether accessed via the mobile App (as defined
            below) or otherwise.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            By using the Site, you (i) agree to be bound by the Terms and (ii)
            represent that you are over the age of eighteen (18) and able to
            form legally binding contracts.
          </SelectedBulletText>
        </SelectedBulletPoint>
      </SelectedList>

      <SelectedCopy bold style={{ marginBottom: 12 }}>
        2. Definitions
      </SelectedCopy>
      <SelectedList>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            The words &ldquo;User,&rdquo; &ldquo;you&rdquo; and
            &ldquo;your&rdquo; refer to the individual or entity that creates an
            EMME Styling account as a Customer and/or Stylist and/or otherwise
            uses the Service. &ldquo;We,&rdquo; &ldquo;us&rdquo; and
            &ldquo;our&rdquo; refer to EMME Styling Corp, a Florida corporation.
            We also refer to our company and our Service as &ldquo;EMME
            Styling&rdquo; depending on the context. &ldquo;Customer&rdquo; also
            refers you as a User who uses the Service (whether or not you
            actually engage with a Stylist). &ldquo;Stylist&rdquo; refers to the
            entity or person that Customers will be connected to for style
            advice after completing such Customer&apos;s registration.
          </SelectedBulletText>
        </SelectedBulletPoint>
      </SelectedList>

      <SelectedCopy bold style={{ marginBottom: 12 }}>
        3. Communications and Rights to Modify Terms
      </SelectedCopy>
      <SelectedList>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            When you visit EMME Styling or send us e-mails, you are
            communicating with us electronically as well as via e-mail, text
            message, calls, video calls, and push notifications. You consent to
            receive communications from us through the means outlined above or
            by posting notices on the Site. You agree that all agreements,
            notices, disclosures and other communications that we provide to you
            electronically or through other means satisfy any legal requirement
            that such communications be in writing. You agree to provide us
            with, and maintain in your EMME Styling account profile, your
            current and active email address.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            EMME Styling may modify any of the Terms at any time by posting them
            on the Site. Changes shall automatically be effective upon posting;
            provided, however, that those changes that EMME Styling, in its sole
            discretion, deems material changes to the Terms will be effective as
            to an existing User upon the earlier of: (1) the agreement of the
            User; or (2) thirty (30) days after notice to the existing User from
            an EMME Styling email address to the User&apos;s email address on
            file with EMME Styling or via other means including, but not limited
            to, a pop-up or banner, message, or other conspicuous notice on the
            EMME Styling website. If you affirmatively agree to the new Terms,
            your agreement will be effective immediately. You agree to make any
            and all necessary arrangements so that you (and not your spam
            filter) receive all emails from EMME Styling email addresses. Your
            continued use of the Service will signify your acceptance of the
            changes. If you do not accept the changes, your sole and exclusive
            remedy is to cancel your account and discontinue using the Site. The
            latest Terms will be posted on the Site, and you should always
            review them prior to using the Site.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            Communications from EMME Styling, including its agents,
            representatives, affiliates, or business partners, may include but
            are not limited to: operational communications concerning your
            account or use of the EMME Styling Site, updates concerning new and
            existing features of the Site, marketing information and promotions
            run by us, our agents, representatives, affiliates, or business
            partners, and any news concerning EMME Styling.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            By entering your telephone number into the EMME Styling Services,
            you expressly agree to receive communications for the purposes
            identified above from us, our agents, representatives, affiliates,
            or business partners, via e-mail, SMS or text messages (including
            Twilio), phone calls, video calls (including Zoom), and push
            notifications. You further expressly agree that these
            communications, including phone calls, SMS or text messages, may be
            generated using automated technology, such as an automatic telephone
            dialing system, or artificial or prerecorded voice, and even if your
            telephone number(s) is listed on any corporate, state, or federal
            Do-Not-Call lists.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            You represent that for the telephone number(s) that you have
            provided to us, you are the current subscriber or customary user and
            that you have the authority to provide the consent described above
            to be contacted at such number(s). You agree to promptly alert us
            whenever you stop using a particular telephone number. Standard
            charges may apply to the receipt of these calls or text messages.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            Opting-out of Promotional Communications. If you wish to opt out of
            promotional emails, you can unsubscribe from our promotional email
            list by following the unsubscribe options in the promotional email
            itself. If you wish to opt out of marketing or promotional calls or
            texts, text back &ldquo;STOP&rdquo; to the number you received
            outreach from, from the mobile device receiving the messages. You
            acknowledge that you are not required to consent to receive
            promotional texts or calls as a condition of using the site or the
            services provided by non-EMME Styling entities.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            Please note that consent is not a condition of using our services
            and consent may be revoked at any time. However, opting out may
            impact your use of the Site. If you wish to opt out of all texts or
            calls from EMME Styling (including operational or transactional
            texts or calls), you can text the word &ldquo;STOPALL&rdquo; to the
            number you received outreach from, from the mobile device receiving
            the messages, however you acknowledge that opting out of receiving
            all texts may impact your use of the Site.
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>•</SelectedBulletSymbol>
          <SelectedBulletText>
            Information collected, including personal information may be shared
            with affiliates and third-party providers, who will use that
            information in accordance with their own privacy policies. Please
            visit our{' '}
            <SelectedLink onPress={navigateToPrivacyPolicy}>
              Privacy Policy
            </SelectedLink>{' '}
            for more information.
          </SelectedBulletText>
        </SelectedBulletPoint>
      </SelectedList>

      <SelectedDivider />
      <SelectedCopy bold style={{ marginBottom: 16 }}>
        B. The Service
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 12 }}>
        4. EMME Styling is a Venue; Third-Party Content
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        The EMME Styling Site is a venue providing styling advice and
        connections to Stylists (virtual and in person). The Site is owned and
        operated by EMME Styling and exists solely to broker relationships
        between Stylists and potential Customers of those Stylists who are
        interested in purchasing the Stylist&apos;s services. EMME Styling is
        not in the business of providing or selling information or education
        that is within any Stylist&apos;s area of expertise. Customers will
        provide certain information that will allow EMME Styling to provide
        customized style preferences to Customers and subsequently connect
        Customers to qualified Stylists (whether virtually or in person).
        Stylists will provide EMME Styling with information regarding their
        styling expertise and EMME Styling will then connect Stylists to
        interested Customers (who will then work with such Stylists virtually or
        in person). Stylists are not employees or agents of EMME Styling but are
        independent service providers using the Site to connect with potential
        Customers requiring their styling expertise and, as such, together with
        Customers, are simply Users of the Site.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME Styling is not involved in the conversations, consultations or, in
        general, engagements by and between Customers and Stylists. EMME Styling
        is not and shall not be responsible for - nor liable in connection with
        - any services provided by Stylists or interactions by and between
        Customers and Stylists (whether virtually or in person). EMME Styling
        disclaims any and all liability (whether foreseeable or not) with regard
        to the foregoing, and all Users of the Site hereby acknowledge this and
        fully release EMME Styling from any and all responsibility or liability
        with regard to any and all interactions by and between any Customer(s)
        and any Stylist(s). You understand and acknowledge that EMME Styling
        cannot and does not edit, modify, filter, screen, monitor, endorse or
        guarantee the content of interactions. EMME Styling shall not be liable
        for any acts or omissions of Stylists, content in communications or the
        ability of Stylists to answer questions. We cannot ensure that a
        Customer and/or a Stylist will complete a transaction. Notwithstanding
        the foregoing, EMME Styling reserves the right, but is not obligated, to
        refuse to post or to remove any content and/or remove any User&apos;s
        access to the Site.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        5. Posts Are Not Private or Confidential; Anonymity
      </SelectedCopy>
      <SelectedCopy>
        The Site is an internet-based forum. Information and materials submitted
        in the content of your calls, questions, answers, requests for
        information, responses, profiles, signatures, qualifications, comments,
        and posts in through the Service and/or other places where Users
        communicate on the Site (collectively &quot;Posts&quot;){' '}
        <SelectedCopy italic>
          is not private or confidential, and it may be read, collected, and
          used by others.
        </SelectedCopy>
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        6. Verification; No Reliance on the Term &ldquo;Stylist&rdquo;
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME Styling has endeavored to find Stylists to work with our Customers
        and has relied on the information provided by the Stylists themselves.
        Use of the term &quot;Stylist&quot; by EMME Styling and on the Site is
        only meant to describe Users who have contracted with EMME Styling to
        provide styling services on the Site and not to guarantee any particular
        level of expertise of these Stylists.
      </SelectedCopy>
      <SelectedCopy>
        The results of the verifications are only as accurate as the information
        provided to and by the Stylists themselves. EMME Styling makes effort to
        verify, but cannot warrant or guarantee a Stylist&apos;s purported
        identity; user identification on the Internet is difficult. For these
        reasons, EMME Styling cannot and does not represent, warrant or
        guarantee the truthfulness or accuracy of the credentials or identities
        of Users and information provided by Users including Stylists. You
        acknowledge that EMME Styling will not be liable for any loss or damages
        caused by your reliance on any information or content contained in
        Posts.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        7. Information Not Advice
      </SelectedCopy>
      <SelectedCopy bold>
        Advice provided by Stylists are to be used by Customers for general
        information purposes only.
      </SelectedCopy>

      <SelectedDivider />
      <SelectedCopy bold style={{ marginBottom: 16 }}>
        C. User Accounts
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        8. User Accounts; Restricted Activities; Suspension or Termination of
        Service
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>User Accounts.</SelectedCopy> When you register as a
        User on the Site, you can establish a user name for access to your
        online account. You are responsible for maintaining the confidentiality
        of your user name, and account data, and you may not share this
        information or your account with anyone else, and you will be solely
        responsible for all acts or omissions that occur under your account. You
        will immediately notify EMME Styling of any unauthorized use of your
        account. You should only create one account on the Site. If your EMME
        Styling account has been suspended or terminated, you may not open
        another account on the Site.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        You agree to keep your contact and billing information (including but
        not limited to email address) up-to-date, and to comply with all billing
        procedures, including providing and maintaining accurate and lawful
        billing information for active EMME Styling accounts.
      </SelectedCopy>

      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Bound by Representatives.</SelectedCopy> You are
        responsible for and bound by the actions of, and transactions entered
        into by, your actual and appointed representatives whether authorized by
        you in writing, verbally, or otherwise to represent you at or with EMME
        Styling. You are responsible for monitoring your appointed
        representatives and protecting the privacy of your username, login
        information and any other account or financial information provided to
        EMME Styling or its vendors and agree to inform us immediately, in
        writing, of any unauthorized use of same. If you are checking in or
        logging in as a representative for anyone other than yourself, you agree
        that you are a duly appointed and authorized representative for that
        person or entity and have authority to bind that person or entity to any
        actions or transactions you conduct.
      </SelectedCopy>

      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Restricted Activities.</SelectedCopy> You agree that
        any content you provide on the Site and your use of our Site or any
        Additional Services initiated through the Site shall not: (a) be
        fraudulent, inaccurate or misleading; (b) infringe any third
        party&apos;s proprietary rights or rights of publicity or privacy (EMME
        Styling can remove unauthorized use of copyrighted material); (c) be
        illegal or violate any federal, state or local law, statute, ordinance,
        regulation or ethical code; (d) be defamatory, trade libelous,
        unlawfully threatening or unlawfully harassing; (e) be obscene or
        contain child pornography or, if otherwise adult in nature or harmful to
        minors, but without containing child pornography; (f) contain any
        viruses, Trojan horses, or other computer programming routines that may
        damage, detrimentally interfere with, surreptitiously intercept or
        expropriate any system, data or personal information; (g) create
        liability for us or cause us to lose (in whole or in part) the services
        of our ISPs or other suppliers; or (h) link directly or indirectly to or
        include descriptions of goods or services that are prohibited under the
        Terms or that you do not have a right to link to or include. You may not
        consummate any transaction that was initiated using our Service that, by
        paying to us a fee, could cause us to violate any applicable law,
        statute, ordinance or regulation. Furthermore, you may not resell or
        make any commercial use of our system or the content on the Site without
        EMME Styling&apos;s prior written consent. We do not knowingly collect
        personal information from children under 18 years of age, and if we
        become aware that a child under the age of 18 has submitted personal
        information to us, we will promptly remove it from our systems.
      </SelectedCopy>

      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Suspension or Termination of Service. </SelectedCopy>
        Customers may terminate your service and account at your sole discretion
        and at any time by written notice via e-mail to{' '}
        <SelectedLink onPress={openSupportEmail}>
          support@emmestyling.com
        </SelectedLink>
        . Terminations typically will be effective within thirty business days
        after our receipt of your termination notice, at which time your account
        will be closed and you will no longer enjoy access to your former
        account. Any fees accrued as of the effective date of termination will
        be payable according to the Terms. The procedure for Stylists to
        terminate their accounts is set forth in the Stylist Agreement.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        At any time, with or without notice, for any or no reason, EMME Styling
        reserves the right to modify or discontinue any portion or all of the
        Site or Services, and to restrict, suspend and terminate Customers&apos;
        and/or Stylist accounts.
      </SelectedCopy>

      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Mobile Terms. </SelectedCopy> Users can access the
        Service through the EMME Styling mobile application (the
        &ldquo;App&rdquo;). Download and use of the App is required. By
        downloading and using one or more versions of the EMME Styling App, you
        agree that in addition to these Terms generally governing your use of
        the Service, the following terms and conditions also specifically apply
        to your use of the App (the &ldquo;Mobile Terms&rdquo;):
      </SelectedCopy>
      <SelectedList>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>(a)</SelectedBulletSymbol>
          <SelectedBulletText>
            You acknowledge that the Mobile Terms and the Agreement is between
            you and EMME Styling, and not any other third-party beneficiary (as
            defined below in (b)) and you understand that EMME Styling is solely
            responsible for the App;
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>(b)</SelectedBulletSymbol>
          <SelectedBulletText>
            Additionally, Apple, Google, Inc., or Microsoft Corporation will be
            a third-party beneficiary to this contract if you access the Service
            via applications developed for Apple iOS, or Android, respectively;
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>(c)</SelectedBulletSymbol>
          <SelectedBulletText>
            Your access and use of the EMME Styling App using these devices is
            subject to terms set forth in the applicable third-party
            beneficiary&apos;s terms of service;
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>(d)</SelectedBulletSymbol>
          <SelectedBulletText>
            The third-party beneficiaries listed above are not parties to this
            Agreement and are not responsible for the provision or support of
            the EMME Styling platform in any way;
          </SelectedBulletText>
        </SelectedBulletPoint>
        <SelectedBulletPoint>
          <SelectedBulletSymbol>(e)</SelectedBulletSymbol>
          <SelectedBulletText>
            If you are accessing the EMME Styling App through an Apple device,
            you acknowledge that Apple and its subsidiaries (collectively,
            &ldquo;Apple&rdquo;) are third party beneficiaries of the Agreement
            and upon your download of and use of the App indicating your assent
            and agreement to the Mobile Terms, Apple will have the right (and
            will be deemed to have accepted the right) to enforce the Mobile
            Terms against you as a third party beneficiary thereof.
          </SelectedBulletText>
        </SelectedBulletPoint>
      </SelectedList>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        9. Charges, Refund and Cancellation Policy
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Pay-Per-Call. </SelectedCopy>
        With the pay-per-call model, you select the Stylist that you are willing
        to pay for to ask questions, and/or talk to. Once you have selected
        Stylist and submitted your payment information, your call will start or
        be booked for a later date and/or time. Customers are charged in one of
        three ways depending on the payment source, as described on the payment
        page: (i) when the call starts; (ii) when the call is scheduled; or
        (iii) when the call is extended.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Rating the Stylist. </SelectedCopy>
        Stylists typically answer Customers&apos; calls quickly. If the Stylist
        requests further information before answering your question, you may
        wish to take advantage of the opportunity to provide more information to
        the Stylist to allow the Stylist to help you better.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        Once the Stylist answers your question and/or talks to you, you will be
        asked to rate the Stylist. If you provide a rating of 1 or 2, EMME
        Styling will watch the recorded call to better access your claims, so
        the Stylist may try to help you better on a later call, if requested. By
        booking a call, you are authorizing EMME Styling to charge your payment
        source (for example, the credit card or PayPal account you provided to
        EMME Styling). A portion of your payments may be paid to the Stylist
        instead of to EMME Styling.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME Styling does not guarantee that you will receive a response from a
        Stylist, or that you will be satisfied with your communication with a
        Stylist. For Pay-Per-Call Customers, in the rare instance that no
        Stylist responds at all to your question, you agree that any amount up
        to $5 that was charged upon posting of your question may be retained by
        EMME Styling in consideration for the Site Access Benefits received. See
        Refunds section below for information on requesting a refund.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>
          Refunds, Cancellations, Abuse and Special Programs.
        </SelectedCopy>
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        Refunds are only granted if you cancel a session more than 24 hours
        before the session is scheduled to occur. For any cancellations less
        than 24 hours of the scheduled session, you will be charged 50% of the
        session costs. No Refunds except as set forth herein. To cancel, you may
        use one of the following methods: write an email to EMME Styling at{' '}
        <SelectedLink onPress={openSupportEmail}>
          support@emmestyling.com
        </SelectedLink>{' '}
        requesting a cancellation.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        10. Receipt of Special Offers and Other Communications
      </SelectedCopy>
      <SelectedCopy>
        By accepting these Terms, you agree to receive coupons, special offers,
        and other communications from EMME Styling per the terms of the{' '}
        <SelectedLink onPress={navigateToPrivacyPolicy}>
          Privacy Policy
        </SelectedLink>
        . You may modify this setting by making the relevant changes to your
        EMME Styling profile.
      </SelectedCopy>

      <SelectedDivider />

      <SelectedCopy bold style={{ marginBottom: 16 }}>
        D. Legal Statements
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        11. Release
      </SelectedCopy>
      <SelectedCopy>
        Users are responsible for their acts and omissions and content placed on
        the Site. EMME Styling will not be liable to Users for any disputes that
        may arise between or among Users, including any disputes by and between
        any Customer(s) and any Stylist(s) (and regardless of whether arising
        out of in-person engagements or virtual engagements).
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        12. Confidentiality
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        You acknowledge that EMME Styling and its licensors and suppliers own
        the rights to the EMME Styling website and the content displayed on the
        Site. You shall not modify, reverse engineer, decompile, disassemble, or
        attempt to derive the source code of the EMME Styling website, any
        software distributed by or assist any other person or entity in doing
        so. You acknowledge that all content, including, but not limited to,
        text, software, music, sound, photographs, video, graphics or other
        material contained in listings, sponsor advertisements or
        email-distributed, commercially produced information presented to you by
        the Service, by EMME Styling, EMME Styling Users, or EMME Styling
        Advertisers or other content providers, is protected by copyrights,
        trademarks, service marks, patents or other proprietary rights and laws.
        You may not modify, copy, reproduce, republish, upload, post, transmit,
        or distribute in any way content available through the Service, whether
        done directly or through intermediaries (including, but not limited to,
        by means of spiders, robots, crawlers, scrapers, framing, iframes or RSS
        feeds) including code and software for commercial purposes. For
        permission to use third-party materials appearing on the Site, please
        contact the copyright owner. You do not acquire ownership rights to any
        content, document or other materials viewed through the Site. The
        posting of information or materials on the Site does not constitute a
        waiver of any right in such information and materials.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        Any access to or use of EMME Styling to design, develop, test, update,
        operate, modify, maintain, support, market, advertise, distribute or
        otherwise make available any program, application or service (including,
        without limitation, any device, technology, product, computer program,
        mobile device application, website, or mechanical or personal service)
        that enables or provides access to, use of, operation of or
        interoperation with EMME Styling (including, without limitation, to
        access content, post content, cross-post content, re-post content,
        respond or reply to content, verify content, transmit content, create
        accounts, verify accounts, use accounts, circumvent and/or automate
        technological security measures or restrictions, or flag content)
        without the prior written authorization of EMME Styling is prohibited.
        This prohibition specifically applies, but is not limited to, software,
        programs, applications and services for use or operation on or by any
        computer and/or any electronic, wireless and/or mobile device,
        technology or product that exists now or in the future.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        If you access EMME Styling or copy, display, distribute, perform or
        create derivative works from content displayed on the Site or other
        intellectual property in violation of the Terms of Service or for
        purposes inconsistent with the Terms of Service, your access, copying,
        display, distribution, performance or derivative work is unauthorized.
        Circumvention of any technological restriction or security measure on
        EMME Styling or any provision of the Terms of Service that restricts
        content, conduct, accounts or access is expressly prohibited. For
        purposes of this paragraph, you agree that cached copies of EMME Styling
        webpages on your computer or computer server constitute
        &ldquo;copies&rdquo; under the Copyright Act, 17 U.S.C. § 101.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        You agree that Posts on the Site, materials, ideas, comments and
        testimonials you submit on the Site or other venues, including, but not
        limited to, the EMME Styling blogs; the EMME Styling venues on{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.facebook.com')}
        >
          www.facebook.com
        </SelectedLink>
        ,{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.instagram.com')}
        >
          www.instagram.com
        </SelectedLink>
        ,{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.twitter.com')}
        >
          www.twitter.com
        </SelectedLink>
        ,{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.linkedin.com')}
        >
          www.linkedin.com
        </SelectedLink>
        ,{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.youtube.com')}
        >
          www.youtube.com
        </SelectedLink>
        ,{' '}
        <SelectedLink
          onPress={() => Linking.openURL('https://www.pinterest.com')}
        >
          www.pinterest.com
        </SelectedLink>
        ,{' '}
        <SelectedLink onPress={() => Linking.openURL('https://www.tiktok.com')}>
          www.tiktok.com
        </SelectedLink>
        ; the Site administrator; or any employee, officer or agent of EMME
        Styling (&ldquo;User Content&rdquo;), will not be considered
        confidential and may be used by EMME Styling, in its sole discretion,
        without any obligation to compensate for use of or to return any
        submitted materials. You also agree that EMME Styling owns, and has the
        right to register in its name, trademarks and service marks for any
        category names that you create on EMME Styling, so do not use a category
        name that you want to reserve for your own benefit. EMME Styling may use
        other trademarks or service marks in lieu of the category names that you
        create.
      </SelectedCopy>
      <SelectedCopy>
        You grant to EMME Styling a non-exclusive, irrevocable, perpetual,
        worldwide, royalty-free, sublicensable (through multiple tiers) right to
        exercise the copyright, publicity, and database rights, including the
        right to use, reproduce, display, edit, copy, modify, transmit, publicly
        perform, extract and create derivative works thereof, that you have in
        your Posts, recorded calls, and User Content, in any media now known or
        not currently known, with respect to any such Posts and other User
        Content.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        13. No Endorsement of Non-EMME Styling Entity; No Relationship with
        Users
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME Styling may endeavor to offer to its Users products and services
        offered by non-EMME Styling entities. The Site may contain links to
        third party websites and service providers (collectively, &ldquo;Third
        Party Services&rdquo;) that are not owned or controlled by EMME Styling.
        EMME Styling has no control over and assumes no responsibility for the
        content, practices, and privacy policies of such Third Party Services.
        EMME Styling encourages you to, and you acknowledge that you are solely
        responsible for, reviewing and complying with any third party terms and
        conditions and privacy policies. EMME Styling cannot and will not censor
        or edit the content of any Third Party Service. Placement of
        information, logos, links or names of such non-EMME Styling entities on
        the Site does not constitute an endorsement or warranty of these
        entities, their products or services. By using this Site, you agree that
        EMME Styling is not responsible for, and you take full responsibility
        for, a decision to visit or patronize any such Third Party Service or
        entity, you hold EMME Styling harmless from any and all liability
        arising from such actions, and you expressly relieve EMME Styling from
        all liability arising from your use of Third Party Services. You further
        acknowledge that no relationship (such as partnership, agent, joint
        venturer, or employee) is created between you and EMME Styling or
        between any User (including Customers and Stylists) and EMME Styling by
        formation of this (or any of the Terms) or by your participation on the
        Site. IT IS THE RESPONSIBILITY OF THE USER TO EVALUATE THE ACCURACY,
        COMPLETENESS AND USEFULNESS OF ANY OPINION, ANSWER OR OTHER CONTENT
        AVAILABLE THROUGH THE SITE, FROM THIRD PARTIES OR OBTAINED FROM A LINKED
        SITE. PLEASE SEEK THE ADVICE OF PROFESSIONALS, AS APPROPRIATE, REGARDING
        THE EVALUATION OF ANY SPECIFIC OPINION, ANSWER, PRODUCT, SERVICE, OR
        OTHER CONTENT.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        14. Information Control and Storage
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        We do not control the information provided by Users, which you may find
        to be offensive, harmful, inaccurate, or deceptive. Please use caution
        and common sense when using the Site. There are also risks of dealing
        with underage persons or people acting under false pretenses. By using
        this Site, you agree to accept such risks and that EMME Styling is not
        responsible for the acts or omissions of Users on the Site.
      </SelectedCopy>
      <SelectedCopy>
        The amount of storage space per User is currently limited. You agree
        that EMME Styling is not responsible or liable for the deletion or
        failure to store content and/or other information.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        15. Exclusion of Warranties; Disclaimers
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME STYLING SERVICES, SOFTWARES, AND RELATED DOCUMENTATION ARE PROVIDED
        &ldquo;AS IS&rdquo; AND WITHOUT ANY WARRANTY OF ANY KIND EITHER EXPRESS
        OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
        MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. NO INFORMATION,
        WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM US THROUGH THIS SITE SHALL
        CREATE ANY WARRANTY, REPRESENTATION OR GUARANTEE NOT EXPRESSLY STATED IN
        THESE TERMS. EMME STYLING DOES NOT REPRESENT OR WARRANT THAT THE SERVICE
        WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR
        THAT THIS SITE OR THE SERVER THAT MAKES IT AVAILABLE, ARE FREE OF
        VIRUSES OR OTHER HARMFUL COMPONENTS. EMME STYLING DOES NOT WARRANT OR
        REPRESENT THAT THE USE OR THE RESULTS OF THE USE OF THE MATERIALS
        AVAILABLE THROUGH THE SERVICE, FROM THIRD PARTIES OR A LINKED SITE WILL
        BE CORRECT, ACCURATE, TIMELY, RELIABLE OR OTHERWISE. SOME STATES OR
        OTHER JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES, SO
        THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU. YOU MAY ALSO HAVE OTHER
        RIGHTS THAT VARY FROM STATE TO STATE AND JURISDICTION TO JURISDICTION.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        UNDER NO CIRCUMSTANCES WILL EMME STYLING BE LIABLE FOR ANY LOSS OR
        DAMAGE CAUSED BY A USER&apos;S RELIANCE ON INFORMATION OBTAINED THROUGH
        THE SITE (INCLUDING, BUT NOT LIMITED TO, ANY INTERACTIONS – WHETHER
        VIRTUAL OR IN PERSON - BY AND BETWEEN ANY CUSTOMER AND ANY STYLIST),
        FROM THIRD PARTIES (SUCH AS STYLISTS OR OTHERS) OR A LINKED SITE, OR
        USER&apos;S RELIANCE ON ANY PRODUCT OR SERVICE OBTAINED FROM A THIRD
        PARTY OR A LINKED SITE. USE OF THIS SITE IS AT USER&apos;S SOLE RISK.
        YOU HEREBY HOLD HARMLESS AND RELEASE EMME STYLING FROM ANY LIABILITY IN
        REGARD TO THE FOREGOING, INCLUDING, BUT NOT LIMITED TO, ANY LIABILITY
        ARISING OUT OF ANY PHYSICAL DAMAGE OR HARM TO ANY PROPERTY OR PERSONS
        AND/OR DEATH.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM
        EMME STYLING OR THROUGH OR FROM THE SERVICES SHALL CREATE ANY WARRANTY.
      </SelectedCopy>
      <SelectedCopy>
        EMME Styling is not involved in the conversations, consultations or, in
        general, engagements by and between Customers and Stylists. EMME Styling
        is not and shall not be responsible for - nor liable in connection with
        - any services provided by Stylists or interactions by and between
        Customers and Stylists (whether virtually or in person). EMME Styling
        disclaims any and all liability (whether foreseeable or not) with regard
        to the foregoing, and all Users of the Site hereby acknowledge this and
        fully release EMME Styling from any and all responsibility or liability
        with regard to any and all interactions by and between any Customer(s)
        and any Stylist(s).
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        16. Limitation of Liabilities
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        IN NO EVENT SHALL EMME STYLING, ITS PARENTS, SUBSIDIARIES, OFFICERS,
        DIRECTORS, SHAREHOLDERS, EMPLOYEES, AGENTS, JOINT VENTURERS,
        CONSULTANTS, SUCCESSORS OR ASSIGNS BE LIABLE FOR ANY INDIRECT, SPECIAL,
        INCIDENTAL, CONSEQUENTIAL, PUNITIVE OR EXEMPLARY DAMAGES (INCLUDING, BUT
        NOT LIMITED TO, LOSS OF BUSINESS, PROFITS, DATA, USE, REVENUE OR OTHER
        ECONOMIC ADVANTAGE), ARISING OUT OF, OR IN CONNECTION WITH, OUR SITE,
        OUR SERVICES OR THE TERMS BASED ON ANY THEORY, EVEN IF ADVISED OF THE
        POSSIBILITY OF SUCH DAMAGES. THE LIMITATION OF DAMAGES SET FORTH ABOVE
        IS A FUNDAMENTAL ELEMENT OF THE BASIS OF THE BARGAIN BETWEEN US AND YOU.
        THIS SITE AND THE INFORMATION WOULD NOT BE PROVIDED WITHOUT SUCH
        LIMITATIONS. IN NO EVENT WILL OUR LIABILITY, AND THE LIABILITY OF OUR
        PARENTS, SUBSIDIARIES, OFFICERS, DIRECTORS, EMPLOYEES, AND SUPPLIERS, TO
        YOU OR ANY THIRD PARTIES IN ANY CIRCUMSTANCE EXCEED THE GREATER OF (A)
        THE AMOUNT OF FEES YOU PAY TO EMME STYLING IN THE 12 MONTHS PRIOR TO THE
        ACTION GIVING RISE TO LIABILITY, OR (B) US$100.00. SOME STATES OR OTHER
        JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR
        CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATIONS AND EXCLUSIONS MAY NOT
        APPLY TO YOU.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        17. Indemnification
      </SelectedCopy>
      <SelectedCopy>
        You agree to indemnify and hold EMME Styling, its parents, subsidiaries,
        officers, directors, shareholders, employees, agents, joint venturers,
        consultants, successors and assigns harmless from and against all
        losses, costs, liabilities and expenses including reasonable
        attorneys&apos; fees, asserted by any third-party that are in any way
        due to, or arising out of, your use of or conduct on the Site,
        including, but not limited to, any actual liability or damage(s) or
        claim (whether threatened or actual) arising out of any interaction(s)
        by and between any Customer and any Stylist (and regardless of whether
        any such interaction is virtual or in person), including, but not
        limited to, any claim(s) arising out of physical harm to any persons or
        property and/or death.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        18. Press Releases and Third-Party Press about EMME Styling
      </SelectedCopy>
      <SelectedCopy>
        The Site may contain press releases and other information about EMME
        Styling. While this information was believed to be accurate as of the
        date prepared, we disclaim any duty or obligation to update this
        information or any press releases. Information about companies other
        than ours contained in the press release or otherwise, should not be
        relied upon as being provided or endorsed by EMME Styling. Likewise,
        third-party press about EMME Styling or the Site should not be relied
        upon as being provided or endorsed by EMME Styling.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        19. Choice of Law
      </SelectedCopy>
      <SelectedCopy>
        Except as set forth in Section D.21 below, the parties agree that this
        Agreement and any and all claims, causes of action or disputes
        (regardless of theory) between you and EMME Styling arising out of or
        related to this Agreement, the Site and any services or content accessed
        therein will be governed by the laws of the State of Florida, without
        regard to conflict or choice of law principles.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        20. Dispute Resolution
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        If you are a Stylist with a dispute, claim or controversy of any sort
        and of any nature (collectively, &ldquo;dispute&rdquo;) against EMME
        Styling in your capacity as a Stylist, then the arbitration agreement in
        the Stylist Agreement applies instead of this one. If your dispute with
        EMME Styling is made in your capacity as User who is not a Stylist, then
        the following dispute resolution provisions apply.
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        This Section may be referred to as the parties&apos; &ldquo;Dispute
        Resolution Agreement.&rdquo; If you have a dispute with EMME Styling or
        if EMME Styling has a dispute with you and such dispute cannot be
        resolved informally, such dispute will be resolved on an individual
        basis in accordance with the provisions set forth below:
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        (a) Informal Resolution. Prior to initiating mediation or arbitration,
        the party with a grievance must:
      </SelectedCopy>
      <SelectedList>
        <SelectedCopy style={{ marginBottom: 8 }}>
          (i) Notify the other party, in writing, of the facts of the dispute
          and all damages claimed. Such a writing must be sent to the
          User&apos;s email address on file with EMME Styling, or Erica Maltz,
          2020 Seabird Way, Riviera Beach, Florida 33404, at{' '}
          <SelectedLink onPress={openDisputeContactEmail}>
            erica@emmestyling.com
          </SelectedLink>
          , whichever is applicable (&ldquo;Dispute Notification&rdquo;);
        </SelectedCopy>
        <SelectedCopy style={{ marginBottom: 8 }}>
          (ii) The party receiving the Dispute Notification has thirty (30) days
          from receipt of the Dispute Notification to respond.
        </SelectedCopy>
        <SelectedCopy style={{ marginBottom: 8 }}>
          (iii) The other party then has 15 days to reply to the response.
        </SelectedCopy>
      </SelectedList>
      <SelectedCopy style={{ marginBottom: 8 }}>
        (b) Mediation. If the dispute is not resolved informally, either party
        may make a demand for mediation, with the other party&apos;s consent, by
        filing a: (i) Request for AAA Online Mediation for Claims under $10,000
        with the American Arbitration Association (&ldquo;AAA&rdquo;); or (ii)
        Request for Mediation with the AAA. To file a request for mediation for
        a claim under $10,000, you must go to the following website:{' '}
        <SelectedLink onPress={openAaaMediationUnder10k}>
          https://services.adr.org/eroom/faces/welcome_and_steps.jspx
        </SelectedLink>
        . If that link does not work, please contact Erica Maltz at EMME Styling
        for updated information. If you are requesting mediation for a claim
        that is worth $10,000 or more, you must go to the following website to
        file such a claim:{' '}
        <SelectedLink onPress={openAaaMediation10kPlus}>
          https://www.adr.org/aaa/faces/services/disputeresolutionservices
        </SelectedLink>
        . If that link does not work, please contact Erica Maltz at EMME Styling
        for updated information. If either party requests mediation and both
        parties consent to mediation, the costs of mediation will be borne
        equally by the parties.
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        (c) ARBITRATION. ANY DISPUTE NOT RESOLVED INFORMALLY OR THROUGH
        MEDIATION SHALL BE RESOLVED BY FINAL AND BINDING ARBITRATION SO LONG AS
        SUCH DISPUTES ARISE IN ANY WAY BECAUSE OF THESE TERMS, ANY TRANSACTION
        WITH EMME STYLING, YOUR USE OF THE SITE AND/OR ANY EMME STYLING SERVICE,
        OR ANY AGREEMENT BETWEEN YOU AND EMME STYLING.
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        YOU ACKNOWLEDGE THAT, BY AGREEING TO THESE TERMS, THIS DISPUTE
        RESOLUTION AGREEMENT IS MADE IN THE COURSE OF INTERSTATE COMMERCE; AND
        THAT THE FEDERAL ARBITRATION ACT, 9 U.S.C. §1 ET. SEQ. (THE
        &ldquo;FAA&rdquo;) AND NOT STATE LAW GOVERNS THE INTERPRETATION AND
        ENFORCEMENT OF THIS DISPUTE RESOLUTION AGREEMENT, INCLUDING THE
        ARBITRABILITY OF DISPUTES, WHICH SHALL BE DECIDED BY THE ARBITRATOR.
        THIS CHOICE OF LAW PROVISION APPLIES ONLY TO THIS DISPUTE RESOLUTION
        AGREEMENT, AND NOT TO THE REST OF THIS AGREEMENT OR TERMS.
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        YOU FURTHER ACKNOWLEDGE THAT YOU AND EMME STYLING ARE EACH WAIVING THE
        RIGHT TO A TRIAL BY JURY REGARDING ANY DISPUTE. IT IS IMPORTANT THAT YOU
        UNDERSTAND THAT THE ARBITRATOR&apos;S DECISION WILL BE BINDING AND MAY
        BE ENTERED AS A JUDGMENT IN ANY COURT OF COMPETENT JURISDICTION.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Arbitration Procedures: </SelectedCopy>
        Disputes shall be resolved by binding arbitration in accordance with the
        arbitration rules of the American Arbitration Association
        (&ldquo;AAA&rdquo;) pursuant to its Consumer Arbitration Rules, as
        modified by the terms in this Dispute Resolution Agreement. Copies of
        these rules can be obtained at the AAA&apos;s website (
        <SelectedLink onPress={openAaaWebsite}>www.adr.org</SelectedLink>) or by
        calling the AAA at 1-800-778-7879. Either party can initiate a demand
        for arbitration. Each party will be responsible for its own costs,
        including paying any AAA filing, administrative and arbitrator fees in
        accordance with AAA rules. The award rendered by the arbitrator may
        include costs of arbitration, reasonable attorneys&apos; fees and
        reasonable costs for Stylist and other witnesses. In the event of any
        inconsistency between this Dispute Resolution Agreement and the
        applicable rules of the AAA, this Dispute Resolution Agreement shall
        govern.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        <SelectedCopy bold>Consent to Jurisdiction in Florida: </SelectedCopy>
        The parties agree that the arbitration will be conducted in Palm Beach
        County, Florida unless you and EMME Styling agree otherwise.
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        (d) CLASS ACTION WAIVER. YOU AND EMME STYLING EXPRESSLY AGREE THAT:
      </SelectedCopy>
      <SelectedList>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          (i) ANY ARBITRATION PROCEEDING WILL TAKE PLACE ON AN INDIVIDUAL BASIS;
        </SelectedCopy>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          (ii)YOU EXPRESSLY WAIVE YOUR ABILITY TO PARTICIPATE AS A PLAINTIFF OR
          CLASS MEMBER IN ANY PURPORTED CLASS, MASS, COLLECTIVE, PRIVATE
          ATTORNEY GENERAL OR OTHER REPRESENTATIVE PROCEEDING;
        </SelectedCopy>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          (iii)THERE SHALL BE NO CLASS CLAIMS, CONSOLIDATION, OR JOINDER ALLOWED
          IN ANY ARBITRATION BETWEEN THE PARTIES;
        </SelectedCopy>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          (iv) IF THIS ARBITRATION AGREEMENT IS FOUND INAPPLICABLE TO YOUR
          DISPUTE WITH EMME STYLING, THIS CLASS ACTION WAIVER WILL CONTINUE TO
          APPLY IN LITIGATION; AND
        </SelectedCopy>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          (v) YOU AGREE THAT THIS CLASS ACTION WAIVER IS AN ESSENTIAL ELEMENT OF
          OUR CONTRACT AND THESE TERMS AND THAT IT MAY NOT BE SEVERED.
        </SelectedCopy>
        <SelectedCopy bold style={{ marginBottom: 8 }}>
          THE ARBITRATOR SHALL NOT CONSOLIDATE CLAIMS OF DIFFERENT USERS INTO
          ONE PROCEEDING, NOR SHALL THE ARBITRATOR HAVE THE POWER TO HEAR
          ARBITRATION AS A CLASS ACTION, COLLECTIVE ACTION, OR REPRESENTATIVE
          ACTION. SIMILARLY, ANY CLAIMS COVERED BY THIS DISPUTE RESOLUTION
          AGREEMENT THAT EMME STYLING MAY HAVE AGAINST USER MAY NOT BE BROUGHT
          AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION,
          COLLECTIVE ACTION OR REPRESENTATIVE ACTION PROCEEDING.
        </SelectedCopy>
      </SelectedList>
      <SelectedCopy>
        (e) Survivability; Right to Consult. This Dispute Resolution Agreement
        shall survive expiration or termination of this Agreement for any
        reason. User acknowledges and agrees that he/she is entering into this
        Dispute Resolution Agreement voluntarily and without any duress or undue
        influence by EMME Styling or anyone else. User further acknowledges and
        agrees that he/she has carefully read this Dispute Resolution Agreement
        and that User has asked any questions needed for User to understand the
        terms, consequences and binding effect of this Dispute Resolution
        Agreement and fully understand it, including that User is waiving
        his/her right to a jury trial. Finally, User agrees that he/she has been
        provided an opportunity to seek the advice of an attorney of User&apos;s
        choice before agreeing to this Dispute Resolution Agreement.
      </SelectedCopy>

      <SelectedCopy bold style={{ marginVertical: 12 }}>
        21. Agreement; Assignment; Miscellaneous
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        The Terms constitute the complete and exclusive statement of the
        Agreement between you and us on the subject matter addressed herein. It
        supersedes any and all prior or contemporaneous agreement, oral or
        written, and any other communications, representations, warranties and
        understanding relating to the subject matter of the Terms. For those
        Users that are Stylists, nothing in this Agreement supersedes the terms
        of the Stylist Agreement, and in the event of a conflict between this
        Agreement (or later versions of this Agreement) and the Stylist
        Agreement, the terms of the Stylist Agreement shall prevail. If there is
        a conflict between an oral or written representation of any EMME Styling
        employee or agent and the Terms (other than modifications to the Terms
        executed in writing by the CEO or authorized-in-fact representative of
        EMME Styling), the Terms will prevail. To the extent the Terms are in
        conflict or inconsistent with one another, this Agreement and the
        Privacy Policy will prevail over other Terms of the Site; also, this
        Agreement, the Privacy Policy, and disclaimers will prevail over FAQs,
        and other rules and policies on the Site. If any provision(s) of the
        Terms is held by an arbitrator or court of competent jurisdiction to be
        contrary to law, then such provision(s) shall be construed, as nearly as
        possible, to reflect the intentions of the parties and the other
        provisions shall remain in full force and effect. EMME Styling&apos;s
        failure to exercise or enforce any of the Terms shall not constitute a
        waiver of EMME Styling&apos;s right to exercise or enforce the Terms as
        to the same or another instance. Headings in this Agreement are for
        purposes of reference only and shall not limit or otherwise affect the
        meaning of the Terms.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        You agree that EMME Styling may assign the Terms to any other entity of
        its choosing, with or without notice to you. You may not assign the
        Terms to any other party for any reason, including, but not limited to,
        because your interaction with other Users of the Site, the pricing
        offered, and fraud mechanisms in place, are based upon individual usage.
        EMME Styling shall not be deemed to have waived any of its rights or
        remedies unless such waiver is in writing and signed by an officer of
        EMME Styling. No delay or omission on the part of EMME Styling in
        exercising any rights or remedies shall operate as a waiver of such
        rights or remedies or any other rights or remedies on future occasions.
        The section titles in the Terms are solely used for the convenience of
        the parties and have no legal or contractual significance.
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        This Section, along with Sections 6, 10 (with respect to amounts
        remaining in your account as of termination), 11, 13, 16, 17, 18, 20,
        and 21 above will survive any termination of this Agreement.
      </SelectedCopy>

      <SelectedDivider />
      <SelectedCopy bold style={{ marginBottom: 16 }}>
        E. Additional Services
      </SelectedCopy>
      <SelectedCopy bold style={{ marginBottom: 8 }}>
        You may be presented with an offer for services outside of the
        Site&apos;s standard Services (&ldquo;Additional Services&rdquo;).
      </SelectedCopy>
      <SelectedCopy style={{ marginBottom: 8 }}>
        EMME Styling is only a venue which facilitates communication and
        connection between Stylists and potential Users of Additional Services.
        It does not provide any of the substance or content of any Additional
        Service initiated through EMME Styling&apos;s platform.
      </SelectedCopy>
      <SelectedCopy bold>
        EMME Styling will in no way be liable for any acts or omissions of
        Stylists in performing Additional Services (or any services) for you.
      </SelectedCopy>
    </SelectedContentContainer>
  );
};

export default Terms;
